import { useState, useEffect, useCallback } from 'react';
import {
  IconBuildingStore,
  IconPigMoney,
  IconReceipt2,
  IconFileDescription,
  IconPhone,
  IconMail,
  IconMapPin,
  IconClock,
  IconCurrencyDollar,
  IconPercentage,
  IconCreditCard,
  IconCreditCardFilled,
  IconMessage,
} from '@tabler/icons-react';
import { ensureMinLoading } from '../Utils/withLoading';
import TipBlue from '../Utils/TipBlue';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: 'none',
  borderRadius: '9999px',
  height: '48px',
  padding: '0 16px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const hintStyle = {
  margin: '6px 0 0 0',
  fontSize: '12px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
  lineHeight: 1.4,
};

export default function ManagerSettings({ notify }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedForm, setSavedForm] = useState(null);
  const [form, setForm] = useState({
    store_name: '',
    store_rfc: '',
    store_phone: '',
    store_email: '',
    store_address: '',
    store_status: true,
    preparation_time: 20,
    currency: 'MXN',
    tax_rate: 16,
    mp_commission_debit_rate: 2.5,
    mp_commission_credit_rate: 3.49,
    ticket_footer: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiFetch(`/settings`);
      if (!res.ok) throw new Error('fetch');
      const data = await res.json();
      const nextForm = {
        store_name: data.store_name || '',
        store_rfc: data.store_rfc || '',
        store_phone: data.store_phone || '',
        store_email: data.store_email || '',
        store_address: data.store_address || '',
        store_status: data.store_status !== false,
        preparation_time: data.preparation_time != null ? data.preparation_time : 20,
        currency: data.currency || 'MXN',
        tax_rate: data.tax_rate != null ? Number(data.tax_rate) : 16,
        mp_commission_debit_rate: data.mp_commission_debit_rate != null ? Number(data.mp_commission_debit_rate) : data.mp_commission_rate != null ? Number(data.mp_commission_rate) : 2.5,
        mp_commission_credit_rate: data.mp_commission_credit_rate != null ? Number(data.mp_commission_credit_rate) : data.mp_commission_rate != null ? Number(data.mp_commission_rate) : 3.49,
        ticket_footer: data.ticket_footer || '',
      };
      setForm(nextForm);
      setSavedForm(nextForm);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    notify?.('loading', 'Guardando configuración...');
    const startedAt = Date.now();

    try {
      const body = {
        store_name: form.store_name.trim(),
        store_rfc: form.store_rfc.trim(),
        store_phone: form.store_phone.trim(),
        store_email: form.store_email.trim(),
        store_address: form.store_address.trim(),
        store_status: form.store_status,
        preparation_time: Math.max(0, Number(form.preparation_time) || 0),
        currency: form.currency.trim() || 'MXN',
        tax_rate: Number(form.tax_rate) || 0,
        mp_commission_debit_rate: Number(form.mp_commission_debit_rate) || 0,
        mp_commission_credit_rate: Number(form.mp_commission_credit_rate) || 0,
        ticket_footer: form.ticket_footer.trim(),
      };

      const res = await apiFetch(`/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
        } catch {}
        notify?.('clear');
        notify?.('error', msg);
        return;
      }

      await ensureMinLoading(startedAt);
      notify?.('clear');
      notify?.('success', 'Configuración guardada correctamente');
      fetchData();
    } catch {
      notify?.('clear');
      notify?.('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = !!savedForm && JSON.stringify(form) !== JSON.stringify(savedForm);

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Configuración
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Datos de la sucursal y cobros del punto de venta
          </p>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 lg:px-6 pt-5 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-center text-sm m-0" style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}>
              Cargando configuración...
            </p>
          </div>
        ) : loadError ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-center text-sm m-0" style={{ fontWeight: 500, color: '#ff453a', fontFamily: FONT }}>
              No se pudieron cargar los datos. Si el problema persiste, reporta el error.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full items-start">
            <div className="flex flex-col gap-6">
              <SectionCard title="Datos de la sucursal" subtitle="Información que identifica tu negocio ante clientes y en el ticket." icon={IconBuildingStore}>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nombre de la tienda" icon={IconBuildingStore} hint="Aparece en el ticket y ante tus clientes." gridColumn="span 2">
                    <input type="text" value={form.store_name} onChange={(e) => handleChange('store_name', e.target.value)} placeholder="Ej. Mi Tienda" style={inputStyle} />
                  </Field>
                  <Field label="RFC" icon={IconFileDescription} hint="Requisito fiscal para emitir facturas.">
                    <input type="text" value={form.store_rfc} onChange={(e) => handleChange('store_rfc', e.target.value)} placeholder="Ej. AST101010101" style={inputStyle} />
                  </Field>
                  <Field label="Teléfono" icon={IconPhone} hint="Dónde pueden llamarte tus clientes.">
                    <input type="tel" value={form.store_phone} onChange={(e) => handleChange('store_phone', e.target.value)} placeholder="Ej. 81 1234 5678" style={inputStyle} />
                  </Field>
                  <Field label="Correo electrónico" icon={IconMail} hint="Para envíos y notificaciones." gridColumn="span 2">
                    <input type="email" value={form.store_email} onChange={(e) => handleChange('store_email', e.target.value)} placeholder="Ej. hola@mitienda.com" style={inputStyle} />
                  </Field>
                  <Field label="Dirección" icon={IconMapPin} hint="Ubicación de tu sucursal para domicilios." gridColumn="span 2">
                    <input type="text" value={form.store_address} onChange={(e) => handleChange('store_address', e.target.value)} placeholder="Calle, número, colonia y ciudad" style={inputStyle} />
                  </Field>
                </div>
                <div className="flex items-center justify-between mt-6">
                  <div>
                    <p className="m-0 font-semibold" style={{ fontSize: '15px', color: 'rgba(255, 255, 255, 0.95)', fontFamily: FONT }}>
                      Estado de la tienda
                    </p>
                    <p className="m-0 mt-1" style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}>
                      {form.store_status ? 'Tienda abierta' : 'Tienda cerrada'}
                    </p>
                  </div>
                  <Toggle on={form.store_status} onChange={(val) => handleChange('store_status', val)} />
                </div>
              </SectionCard>

              <SectionCard title="Ticket" subtitle="Texto opcional que aparece al pie de cada ticket." icon={IconReceipt2}>
                <Field label="Mensaje del ticket" icon={IconMessage} hint="Despedida o nota que acompaña cada ticket impreso.">
                  <textarea
                    value={form.ticket_footer}
                    onChange={(e) => handleChange('ticket_footer', e.target.value)}
                    placeholder="Ej. ¡Gracias por tu compra!"
                    rows={3}
                    style={{ ...inputStyle, height: 'auto', borderRadius: '16px', padding: '12px 16px', resize: 'none' }}
                  />
                </Field>
              </SectionCard>
            </div>

            <div className="flex flex-col gap-6">
              <SectionCard title="Ventas y cobros" subtitle="Parámetros usados en el punto de venta." icon={IconPigMoney}>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Tiempo de preparación" icon={IconClock} hint="Cuánto tardas en tener el pedido listo.">
                    <SuffixedInput suffix="min" type="number" min="0" value={form.preparation_time} onChange={(e) => handleChange('preparation_time', e.target.value)} />
                  </Field>
                  <Field label="Moneda" icon={IconCurrencyDollar} hint="Siglas de tu moneda: MXN, USD, EUR...">
                    <input type="text" value={form.currency} onChange={(e) => handleChange('currency', e.target.value)} placeholder="MXN" style={inputStyle} />
                  </Field>
                  <Field label="Impuesto" icon={IconPercentage} hint="IVA que se cobra en cada venta.">
                    <SuffixedInput suffix="%" type="number" min="0" step="0.01" value={form.tax_rate} onChange={(e) => handleChange('tax_rate', e.target.value)} />
                  </Field>
                  <Field label="Comisión débito" icon={IconCreditCard} hint="Lo que tu banco cobra por pagos con tarjeta de débito.">
                    <SuffixedInput suffix="%" type="number" min="0" step="0.01" value={form.mp_commission_debit_rate} onChange={(e) => handleChange('mp_commission_debit_rate', e.target.value)} />
                  </Field>
                  <Field label="Comisión crédito" icon={IconCreditCardFilled} hint="Lo que tu banco cobra por pagos con tarjeta de crédito.">
                    <SuffixedInput suffix="%" type="number" min="0" step="0.01" value={form.mp_commission_credit_rate} onChange={(e) => handleChange('mp_commission_credit_rate', e.target.value)} />
                  </Field>
                </div>
              </SectionCard>
            </div>
          </div>
        )}
      </div>
      <div className="shrink-0 px-4 lg:px-6 py-4 border-t border-white/[0.16] flex items-center justify-between gap-4">
        <TipBlue>Recuerda dar clic en Guardar cada vez que modifiques un campo para aplicar los cambios.</TipBlue>
        <button
          onClick={handleSave}
          disabled={loading || saving || !hasChanges}
          className="flex items-center justify-center h-12 px-8 rounded-full border-none cursor-pointer touch-manipulation select-none font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
          style={{
            backgroundColor: '#0071e3',
            color: '#fff',
            fontSize: '15px',
            letterSpacing: '-0.01em',
            fontFamily: FONT,
            opacity: loading || saving || !hasChanges ? 0.5 : 1,
            cursor: loading || saving || !hasChanges ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="rounded-3xl p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}>
      <div className="flex items-center gap-3 mb-1">
        <div
          className="flex items-center justify-center shrink-0"
          style={{ width: '36px', height: '36px', borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
        >
          <Icon size={18} color="rgba(255, 255, 255, 0.9)" />
        </div>
        <h2 className="m-0 font-semibold" style={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.95)', fontFamily: FONT }}>
          {title}
        </h2>
      </div>
      <p className="m-0 mb-5" style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT, lineHeight: 1.4 }}>
        {subtitle}
      </p>
      {children}
    </div>
  );
}

function Field({ label, icon: Icon, hint, gridColumn, children }) {
  return (
    <div style={gridColumn ? { gridColumn } : undefined}>
      <div className="flex items-center gap-1.5" style={{ marginBottom: '8px' }}>
        {Icon && <Icon size={15} color="rgba(255, 255, 255, 0.45)" />}
        <label className="m-0" style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)', fontFamily: FONT }}>
          {label}
        </label>
      </div>
      {children}
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  );
}

function SuffixedInput({ suffix, ...props }) {
  return (
    <div style={{ position: 'relative' }}>
      <input {...props} style={{ ...inputStyle, paddingRight: '52px' }} />
      <span
        style={{
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '13px',
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.45)',
          fontFamily: FONT,
          pointerEvents: 'none',
        }}
      >
        {suffix}
      </span>
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button
      onClick={() => onChange(!on)}
      className="flex items-center bg-transparent border-none cursor-pointer p-0"
    >
      <div
        className="relative flex items-center"
        style={{
          width: '44px',
          height: '24px',
          borderRadius: '9999px',
          backgroundColor: on ? '#0a84ff' : 'rgba(255, 255, 255, 0.12)',
          transition: 'background-color 0.2s ease',
        }}
      >
        <div
          className="absolute bg-white rounded-full"
          style={{
            width: '18px',
            height: '18px',
            top: '3px',
            left: on ? '23px' : '3px',
            transition: 'left 0.2s ease',
          }}
        />
      </div>
    </button>
  );
}